import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Circle, CircleCheck, KeyRound, LoaderCircle, ShieldCheck, TriangleAlert } from 'lucide-react';
import type { TokenInspection, TokenPasswordResponse } from '@ana/contracts';
import { ApiError, api, errorMessage } from '../../api/client.ts';
import { useSession } from '../../auth/session.tsx';
import { PasswordInput } from '../../components/PasswordInput.tsx';
import { Loading } from '../../components/ui.tsx';
import { MSG } from '../../content/messages.ts';
import { AuthShell, FormError, restoreFocus } from './AuthShell.tsx';
import { MAX_PASSWORD_LENGTH, passwordChecklist, tokenFromHash } from './password.ts';

type View =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'error'; message: string }
  | { kind: 'form'; info: TokenInspection }
  | { kind: 'done'; info: TokenInspection };

const MFA_NOTICE = 'No primeiro acesso, o sistema vai pedir a ativação da verificação em duas etapas.';

/**
 * "/definir-senha#token=…": first password of an invitation or a new password after a reset request. The token
 * authorizes the page by itself (it opens with or without a session) and is kept only in memory.
 */
export function SetPasswordPage() {
  const { hash } = useLocation();
  const [token, setToken] = useState(() => tokenFromHash(hash));
  // A newer link opened in this tab changes only the fragment, so the browser does not reload the page: adopt its
  // token, and the key starts the page over for it (inspection, form and messages of the old link are dropped).
  useEffect(() => {
    const next = tokenFromHash(hash);
    if (next) setToken(next);
  }, [hash]);
  return <SetPasswordFlow key={token ?? ''} token={token} />;
}

function SetPasswordFlow({ token }: { token: string | null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, logout } = useSession();
  const [view, setView] = useState<View>(token ? { kind: 'loading' } : { kind: 'invalid' });
  const [tries, setTries] = useState(0);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const checklistId = useId();
  const passwordId = useId();
  const doneRef = useRef<HTMLDivElement>(null);
  const invalidRef = useRef<HTMLHeadingElement>(null);

  // Drop the fragment from the address bar and from this history entry: the link must not stay visible on screen
  // or be reopened from the history after it was used.
  useEffect(() => {
    if (location.hash) {
      void navigate(
        { pathname: location.pathname, search: location.search, hash: '' },
        { replace: true, state: location.state as unknown },
      );
    }
  }, [location, navigate]);

  useEffect(() => {
    if (!token) return;
    const ctrl = new AbortController();
    api<TokenInspection>('/auth/token/inspect', {
      method: 'POST',
      body: { token },
      signal: ctrl.signal,
    }).then(
      (info) => setView({ kind: 'form', info }),
      (e: unknown) => {
        if (ctrl.signal.aborted) return;
        // 400 is the server's single answer for unknown, used, revoked or expired links (nothing to tell apart).
        setView(
          e instanceof ApiError && e.status === 400
            ? { kind: 'invalid' }
            : { kind: 'error', message: errorMessage(e) },
        );
      },
    );
    return () => ctrl.abort();
  }, [token, tries]);

  // Both views replace what had the focus (the form, the spinner): move it to the news so it is read.
  useEffect(() => {
    if (view.kind === 'done') doneRef.current?.focus();
    if (view.kind === 'invalid') invalidRef.current?.focus();
  }, [view.kind]);

  const info = view.kind === 'form' || view.kind === 'done' ? view.info : null;
  const checks = passwordChecklist(password, confirm, info?.email ?? '');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token || view.kind !== 'form' || !checks.valid) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<TokenPasswordResponse>('/auth/token/password', {
        method: 'POST',
        body: { token, newPassword: password },
      });
      setPassword('');
      setConfirm('');
      setView({ kind: 'done', info: { ...view.info, email: r.email } });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'TOKEN_INVALID') {
        setView({ kind: 'invalid' });
      } else {
        setError(errorMessage(err));
        setAttempt((n) => n + 1);
        restoreFocus(document.getElementById(passwordId));
      }
    } finally {
      setBusy(false);
    }
  };

  const goToLogin = async (email: string) => {
    setLeaving(true);
    // A session open in this browser may belong to someone else, and the server has just closed the sessions of
    // this account anyway: leave it so the login starts clean.
    if (session) await logout();
    void navigate('/', { replace: true, state: { passwordSet: true, email } });
  };

  const heading = info?.purpose === 'INVITE' ? 'Defina sua senha' : 'Crie uma nova senha';

  return (
    <AuthShell>
      {view.kind === 'loading' ? <Loading what="Verificando o link" /> : null}

      {view.kind === 'invalid' ? (
        <div className="auth-enter">
          <h2 ref={invalidRef} tabIndex={-1}>
            Este link não é mais válido
          </h2>
          <p className="muted small">
            O link pode ter expirado, já ter sido usado ou ter sido substituído por um mais recente. Para
            criar uma nova senha, solicite um novo link. Se era um convite, peça à administração do sistema
            que o reenvie.
          </p>
          <p>
            <Link className="btn primary block" to="/esqueci-a-senha">
              Solicitar novo link
            </Link>
          </p>
          <p className="help">
            <Link to="/">Voltar para o login</Link>
          </p>
        </div>
      ) : null}

      {view.kind === 'error' ? (
        <div className="auth-enter">
          <h2>Não foi possível verificar o link</h2>
          <div className="alert danger" role="alert">
            <TriangleAlert size={18} aria-hidden="true" />
            <span>{view.message}</span>
          </div>
          <button
            type="button"
            className="btn primary block"
            onClick={() => {
              setView({ kind: 'loading' });
              setTries((n) => n + 1);
            }}
          >
            {MSG.common.retry}
          </button>
        </div>
      ) : null}

      {view.kind === 'form' ? (
        <form className="auth-enter" onSubmit={(e) => void submit(e)} aria-labelledby="set-title" noValidate>
          <h2 id="set-title">{heading}</h2>
          <p className="muted small auth-identity">
            <strong>{view.info.displayName}</strong>
            {view.info.email}
          </p>
          {view.info.requiresMfaSetup ? (
            <div className="alert info" role="note">
              <ShieldCheck size={18} aria-hidden="true" />
              <span>{MFA_NOTICE}</span>
            </div>
          ) : null}
          <FormError error={error} attempt={attempt} />
          {/* Lets password managers save the new password under the right account. */}
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={view.info.email}
            readOnly
            hidden
          />
          <PasswordInput
            id={passwordId}
            label="Nova senha"
            autoComplete="new-password"
            maxLength={MAX_PASSWORD_LENGTH}
            required
            autoFocus
            aria-describedby={checklistId}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <PasswordInput
            label="Confirme a nova senha"
            autoComplete="new-password"
            maxLength={MAX_PASSWORD_LENGTH}
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <ul
            id={checklistId}
            className="password-checklist"
            aria-label="Requisitos da senha"
            aria-live="polite"
          >
            {checks.items.map((item) => (
              // aria-atomic: only the state word changes, and a live region reads only what changed — the whole
              // item is read instead, otherwise the announcement would be a bare "(atendido)".
              <li
                key={item.id}
                className={item.ok ? 'ok' : undefined}
                data-check={item.id}
                aria-atomic="true"
              >
                {item.ok ? (
                  <CircleCheck size={16} aria-hidden="true" />
                ) : (
                  <Circle size={16} aria-hidden="true" />
                )}
                <span>
                  {item.label}
                  <span className="visually-hidden">{item.ok ? ' (atendido)' : ' (pendente)'}</span>
                </span>
              </li>
            ))}
          </ul>
          <button className="btn primary block" type="submit" disabled={busy || !checks.valid}>
            {busy ? (
              <LoaderCircle size={18} className="spin" aria-hidden="true" />
            ) : (
              <KeyRound size={18} aria-hidden="true" />
            )}
            {busy ? 'Salvando…' : view.info.purpose === 'INVITE' ? 'Definir senha' : 'Salvar nova senha'}
          </button>
        </form>
      ) : null}

      {view.kind === 'done' ? (
        <div className="auth-enter">
          <h2>{heading}</h2>
          <div className="alert ok" ref={doneRef} tabIndex={-1}>
            <CircleCheck size={18} aria-hidden="true" />
            <span>
              {view.info.purpose === 'INVITE'
                ? 'Senha definida.'
                : 'Senha definida. As sessões abertas com a senha anterior foram encerradas.'}
            </span>
          </div>
          {view.info.requiresMfaSetup ? <p className="small muted">{MFA_NOTICE}</p> : null}
          <button
            type="button"
            className="btn primary block"
            disabled={leaving}
            onClick={() => void goToLogin(view.info.email)}
          >
            Ir para o login
          </button>
        </div>
      ) : null}
    </AuthShell>
  );
}
